import React, { Fragment, useState, useEffect } from "react";
import _ from "lodash";
import moment from "moment";
import classnames from "classnames";
import {
  Row,
  Col,
  Form,
  Input,
  Label,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ButtonGroup,
  InputGroup,
  InputGroupText,
} from "reactstrap";
import { DebounceInput } from "react-debounce-input";
import DatePicker from "react-datepicker";
import Select from "react-select";
import Alert from "react-s-alert-v3";
import ToggleButton from "react-toggle-button";
import { constants } from "../../utils";
import { api, ui_helpers } from "../../helpers";
import { CalculatorTable } from "../";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { SalespersonEdit, PriceAdjustmentEditor } from "./";
import { default as useContractStore } from "./utils/useContractStore";

export default function ContractPricing(props) {
  const TAX_ENTRY_MODES = {
    RATE: "rate",
    AMOUNT: "amount"
  };
  const [downPaymentOverPayment, setDownPaymentOverPayment] = useState(false);
  const [lastPromoCodeChecked, setLastPromoCodeChecked] = useState('');
  const [taxEntryMode, setTaxEntryMode] = useState(TAX_ENTRY_MODES.RATE);

  const {
    contractId,
    companyId,
    regionId,
    subtotal,
    taxExemptionForm,
    setTaxExemptionForm,
    isTaxExempt,
    setIsTaxExempt,
    promotionCode,
    setPromotionCode,
    promotionCodeTemp,
    setPromotionCodeTemp,
    salesperson,
    otherSalesperson,
    setSalesperson,
    salespeople,
    setInitialPaymentAmount,
    setIncludedInitialPayment,
    sideFeePayment,
    setSideFeePayment,
    unitPrice,
    taxRate,
    taxRateString,
    setTaxRateString,
    widthFeet,
    optedOutOfDamageLiabilityWaiver,
    setOptedOutOfDamageLiabilityWaiver,
    setDamageLiabilityWaiverAmount,
    damageLiabilityWaiverAmount,
    priceAdjustments,
    productType,
    deliveryCounty,
    setDeliveryCounty,
    dueDays,
    paymentDueDayOfMonth,
    setPaymentDueDayOfMonth,
    estimatedDeliveryDate,
    setEstimatedDeliveryDate,
    fieldWarnings,
    setFieldWarnings,
    screenWarnings,
    selectedTermInfo,
    setSelectedTermInfo,
    initialPaymentAmount,
    taxAmount,
    setTaxAmount,
    manufacturerId,
    rtoTaxExemptionEnabled,
    resolveWidthFeet
  } = useContractStore();

  useEffect(() => {
    if (!promotionCode.length)
      return;
    checkTermPromoAvailability(promotionCode);
  }, [selectedTermInfo]);

  useEffect(onEvaluateDownPayment, [selectedTermInfo, promotionCode]);

  function onApplyPromoCode() {
    if (!promotionCodeTemp) {
      setPromotionCode("");
      setFieldWarnings(_.reject(fieldWarnings, (w) => w.field === "promoCodeTermCheck"));
    } else if (promotionCodeTemp && _.trim(promotionCodeTemp).length) {
      checkTermPromoAvailability(promotionCodeTemp);
    }
  }

  // function onApplyPromoCode() {
  //   if (promotionCodeTemp && promotionCodeTemp.length) {
  //     checkTermPromoAvailability(promotionCodeTemp);
  //   }
  // }

  function onEvaluateDownPayment() {
    if (!selectedTermInfo) return;
    const minRequired = selectedTermInfo.requiredForDeliveryAmount_NOPR + ui_helpers.sideFeeSum(priceAdjustments);
    if (Math.round(initialPaymentAmount + sideFeePayment) > Math.round(minRequired)) {
      setDownPaymentOverPayment(true);
    }
  }

  const onSelectedTermChange = (term) => {
    setSelectedTermInfo(term);
    setIncludedInitialPayment(false);
    setInitialPaymentAmount(
      term.requiredForDeliveryAmount_NOPR
    );
    const sideFeeSum = ui_helpers.sideFeeSum(priceAdjustments);
    setSideFeePayment(sideFeeSum);
  }

  function checkTermPromoAvailability(code, term) {
    if (!selectedTermInfo || code === lastPromoCodeChecked)
      return;
    api.post("Contract/PromoCodeTermCheck", {
        promoCode: code,
        companyId: companyId,
        contractTermId: selectedTermInfo.contractTermId,
        productTypeId: productType ? productType.value : null,
        activeOnly: true,
      }).then((res) => {
        setLastPromoCodeChecked(code);
        if (res.data && !res.data.success) {
          setFieldWarnings([{ field: "promoCodeTermCheck", text: res.data.message }]);
        } else {
          setPromotionCode(code);
          setFieldWarnings(_.reject(fieldWarnings, (w) => w.field === "promoCodeTermCheck"));
        }
      }).catch((err) => console.error(err));
  }

  function onDownloadAttachment(contractId, key = null, onComplete = null) {
    const pdfWindow = window.open();
    // null document key means download all
    api.downloadAndOpenFile(
        "contract/DownloadContractDocument",
        { referenceId: contractId, documentKey: key },
        pdfWindow,
        (e) => {
          console.error(e);
          if (pdfWindow) {
            pdfWindow.close();
          }
        },
      constants.MIME_PDF,
      onComplete
    );
  }

  function onUploadTaxExemptionForm(file) {
    const filePayload = {
      referenceId: contractId,
      file: file,
      typeOfAttachment: constants.ATTACHMENT_TYPE_IDS.TaxExemptionForm,
    };
    setTaxExemptionForm(filePayload);
  }

  function findDeliveryCounty() {
    if (deliveryCounty === "") {
      Alert.error("Delivery County Name is required");
      return;
    }
    api
      .post("Contract/FindTaxRate", {
      contractId: contractId,
      isInCityLimits: isInCityLimits,
      deliveryCounty: deliveryCounty,
    })
    .then((res) => {
      if (res.data && res.data.success) {
        const taxRateValueString = res.data.message.taxRateValue * 100.0;
        setTaxRate(res.data.message.taxRateValue);
        setTaxRateString(taxRateValueString ? taxRateValueString : "");
      }
    })
    .catch((error) => {
      Alert.error("An error occurred during county lookup");
      console.error(error);
    });
  }

  const ldwOptions = [
    {label: `${ui_helpers.formatCurrency(damageLiabilityWaiverAmount)} / month`, value: false},
    {label: "OPT OUT of LDW", value: true},
  ];

  function getIncludedAdjustmentsSum() {
    const list = _.filter(priceAdjustments, pa => pa.includeInUnitPrice);
    return list.length
      ? _.sumBy(list, pa => parseFloat(pa.amount))
      : 0.0;
  }

  function setInitialAmountToMinimum() {
    const minPmt =
      selectedTermInfo.requiredForDeliveryAmount_NOPR + ui_helpers.sideFeeSum(priceAdjustments);
    setInitialPaymentAmount(minPmt);
    setDownPaymentOverPayment(false);
  }


  const wFt = parseFloat(resolveWidthFeet());
  let downPaymentString = 0.00;
  if (parseFloat(initialPaymentAmount)) {
    downPaymentString = parseFloat(initialPaymentAmount).toFixed(2);
  }

  return (
    <Row>
      <Col xs="4">
        <Modal isOpen={downPaymentOverPayment} centered>
          <ModalHeader>Possible Overpayment</ModalHeader>
          <ModalBody>
            The "Down Payment Paid Today" is more than the minimum required for delivery.
          </ModalBody>
          <ModalFooter>
            <Row>
              <ButtonGroup>
                <Button
                  className={"bg-cf-success border-success"}
                  onClick={() => {
                    setDownPaymentOverPayment(false);
                    setIncludedInitialPayment(true);
                  }}>
                  Continue with {initialPaymentAmount} down
                </Button>
                <Button onClick={setInitialAmountToMinimum}>Reduce to the minimum</Button>
              </ButtonGroup>
            </Row>
          </ModalFooter>
        </Modal>
        <Form className="mt-2">
          <Row>
            <Col>
              <div className="mb-1">
                <Row>
                  <Col md={"12"} lg={"5"}>
                    <SalespersonEdit
                      salespeople={salespeople}
                      salesperson={salesperson}
                      otherSalesperson={otherSalesperson}
                      onChange={(newSalesperson) => setSalesperson(newSalesperson)}
                      onDeleteSalesperson={props.onDeleteSalesperson}
                    />
                  </Col>
                  <Col md={"6"} lg={"3"}>
                    <Row>
                      <Col>
                        <Label>Unit Price {ui_helpers.requiredStar()}</Label>
                      </Col>
                    </Row>
                    <Row>
                      <Col>
                        <DebounceInput
                          type="text"
                          maxLength="15"
                          minLength="2"
                          title={screenWarnings.unitPrice ? screenWarnings.unitPrice : null}
                          className={classnames(
                            { invalid: screenWarnings.unitPrice ? true : false },
                            "text-end form-control"
                          )}
                          value={ui_helpers.formatCurrency(unitPrice)}
                          disabled
                          readOnly
                          debounceTimeout={900}
                        />
                      </Col>
                    </Row>
                  </Col>
                  <Col md={"6"} lg={"4"}>
                    <Row>
                      <Col sm="6">
                        <Label>Tax {taxEntryMode === TAX_ENTRY_MODES.RATE
                            ? "Rate"
                            : "$$$"
                          } {ui_helpers.requiredStar()}</Label>
                      </Col>
                      <Col sm="6">
                        <Button outline className="float-end"
                          style={{ fontSize: "8pt", padding: "2px" }}
                          onClick={() => setTaxEntryMode((oldValue) => oldValue === TAX_ENTRY_MODES.RATE ? TAX_ENTRY_MODES.AMOUNT : TAX_ENTRY_MODES.RATE)}>
                          {taxEntryMode === TAX_ENTRY_MODES.RATE
                            ? "Enter Amount"
                            : "Enter Rate"
                          }
                        </Button>
                      </Col>
                    </Row>
                    <Row>
                      <Col>
                        <InputGroup>
                          {taxEntryMode === TAX_ENTRY_MODES.RATE
                            ? (<Fragment>
                                <DebounceInput
                                  type="text"
                                  minLength="1"
                                  name="taxRate"
                                  title={screenWarnings.taxRate ? screenWarnings.taxRate : null}
                                  readOnly={isTaxExempt}
                                  style={{ width: "68px", float: "left" }}
                                  className={classnames("text-end form-control", {
                                    invalid: screenWarnings.taxRate ? true : false
                                  })}
                                  maxLength="8"
                                  value={isTaxExempt ? 0 : taxRateString || 0}
                                  onChange={(e) => setTaxRateString(e.target.value)}
                                  debounceTimeout={900}
                                />
                                <InputGroupText>%</InputGroupText>
                              </Fragment>)
                              : (
                                <DebounceInput
                                  type="text"
                                  maxLength="25"
                                  minLength="1"
                                  value={taxAmount}
                                  className="text-end form-control"
                                  readOnly={isTaxExempt}
                                  debounceTimeout={300}
                                  onChange={(e) => setTaxAmount(e.target.value)}
                                />
                              )
                            }
                        </InputGroup>
                      </Col>
                    </Row>
                  </Col>
                </Row>
                {rtoTaxExemptionEnabled && (
                <Row>
                  <Col>
                    <Row>
                      <Col className={"d-flex justify-content-end align-items-center"}>
                        <ToggleButton
                          value={isTaxExempt}
                          inactiveLabel={""}
                          activeLabel={""}
                          onToggle={(val) => setIsTaxExempt(!val)}
                        />
                        <div className={"ms-2"}>Tax Exempt</div>
                      </Col>
                    </Row>
                  </Col>
                </Row>
                )}
                <Row>
                  <Col>
                    {isTaxExempt ? (
                      <>
                        <Row className="d-flex">
                          <Label>Tax Exemption Form {ui_helpers.requiredStar()}</Label>
                        </Row>
                        <Row>
                          {taxExemptionForm ? (
                            <>
                              {taxExemptionForm.file ? (
                                <span>{taxExemptionForm.file?.name ?? "Tax Exemption Form"}</span>
                              ) : null}
                              {taxExemptionForm.name ? (
                                <Button
                                  color="link"
                                  className="download-link text-start"
                                  onClick={() => onDownloadAttachment(taxExemptionForm.key)}>
                                  {taxExemptionForm.name}
                                </Button>
                              ) : null}
                            </>
                          ) : (
                            <input
                              type="file"
                              id="myFile"
                              name="taxExemptionForm"
                              accept={constants.MIME_PDF}
                              onChange={(e) => onUploadTaxExemptionForm(e.target.files[0])}
                            />
                          )}
                        </Row>
                      </>
                    ) : null}
                  </Col>
                </Row>
              </div>
              <PriceAdjustmentEditor
                addPriceAdjustment={props.addPriceAdjustment}
                deletePriceAdjustment={props.deletePriceAdjustment}
              />
              <div className="mb-1">
                <Row>
                  <Col xs="6" className="pt-1">
                    <Label>Delivery County {ui_helpers.requiredStar()}</Label>
                  </Col>
                  <Col xs="6">
                    <Input
                      type="text"
                      title={screenWarnings.deliveryCounty ? screenWarnings.deliveryCounty : null}
                      className={classnames(
                        { invalid: screenWarnings.deliveryCounty ? true : false },
                        "form-control"
                      )}
                      bsSize="sm"
                      value={deliveryCounty}
                      onChange={(e) => setDeliveryCounty(e.target.value)}
                    />
                  </Col>
                </Row>
              </div>
              <div className="mb-1"></div>
              <div className="mb-1">
                <Row>
                  <Col xs="6" className="pt-1">
                    <Label>Liability Damage Waiver {ui_helpers.requiredStar()}</Label>
                  </Col>
                  <Col xs="6" style={{ padding: "0 12px" }}>
                    <Select
                      options={ldwOptions}
                      value={_.find(ldwOptions, (o) => o.value === optedOutOfDamageLiabilityWaiver)}
                      onChange={(x) => setOptedOutOfDamageLiabilityWaiver(x.value)}
                    />
                  </Col>
                </Row>
              </div>
              <div className="mb-1">
                <Row>
                  <Col xs="6" className="pt-1">
                    <Label>Preferred Due Date {ui_helpers.requiredStar()}</Label>
                  </Col>
                  <Col xs="6" style={{ padding: "0 12px" }}>
                    <Select
                      options={dueDays}
                      value={_.find(dueDays, (d) => d.value === paymentDueDayOfMonth)}
                      onChange={(x) => setPaymentDueDayOfMonth(x.value)}
                    />
                  </Col>
                </Row>
              </div>
              <div className="mb-1">
                <Row>
                  <Col xs="6" className="pt-1">
                    <Label>Estimated Delivery Date</Label>
                  </Col>
                  <Col xs="6">
                    <div className="datePickerDiv">
                      <DatePicker
                        title={
                          screenWarnings.estimatedDeliveryDate
                            ? screenWarnings.estimatedDeliveryDate
                            : null
                        }
                        className={classnames(
                          { invalid: screenWarnings.estimatedDeliveryDate ? true : false },
                          "form-control"
                        )}
                        selected={
                          estimatedDeliveryDate ? moment(estimatedDeliveryDate).toDate() : null
                        }
                        onChange={(date) => setEstimatedDeliveryDate(date ? moment(date) : null)}
                        showMonthDropdown
                        showYearDropdown
                        isClearable
                        withPortal
                        dropdownMode="select"
                      />
                    </div>
                  </Col>
                </Row>
              </div>
              <div className="mb-1">
                <Row>
                  <Col xs="6" className="pt-1">
                    <Label>Promo Code</Label>
                  </Col>
                  <Col xs="6">
                    {/*<InputGroup size="sm">*/}
                      <Input
                        type="text"
                        maxLength="20"
                        value={promotionCodeTemp}
                        // style={{width: "75%"}}
                        title={screenWarnings.promotionCode ? screenWarnings.promotionCode : null}
                        className={classnames(
                          { invalid: screenWarnings.promotionCode ? true : false }
                        )}
                        onChange={(e) => setPromotionCodeTemp(e.target.value)}
                        onBlur={onApplyPromoCode}
                      />
{/*                      {promotionCodeTemp ? (
                        <Button
                          color="primary"
                          className="text-light"
                          title="Verify Promo"
                          onClick={onApplyPromoCode}>
                          <FontAwesomeIcon icon="check-circle" />
                        </Button>
                      ) : null}
                    </InputGroup>
*/}
                    {screenWarnings.promoCodeTermCheck ? (
                      <span className="mt-2 promo-check-small text-secondary">
                        {screenWarnings.promoCodeTermCheck}
                      </span>
                    ) : null}
                  </Col>
                </Row>
              </div>
              <div className="mb-1">
                <Row>
                  <Col xs="6" className="pt-1">
                    <Label>Down Payment Paid Today {ui_helpers.requiredStar()}</Label>
                  </Col>
                  <Col xs="6">
                    <DebounceInput
                      type="text"
                      maxLength="15"
                      title={
                        screenWarnings.initialPaymentAmount
                          ? screenWarnings.initialPaymentAmount
                          : null
                      }
                      className={classnames(
                        { invalid: screenWarnings.initialPaymentAmount ? true : false },
                        "text-end form-control"
                      )}
                      value={downPaymentString}
                      minLength={2}
                      debounceTimeout={900}
                      onChange={(e) => setInitialPaymentAmount(ui_helpers.cleanseCurrencyCharacters(e.target.value))}
                    />
                    {sideFeePayment
                      ? (<span style={{float: "right", fontStyle: "italic"}}> + {ui_helpers.formatCurrency(sideFeePayment)} side fees</span>)
                      : null
                    }
                  </Col>
                </Row>
              </div>
            </Col>
          </Row>
        </Form>
      </Col>
      <Col xs="8">
        {parseFloat(unitPrice) > 10.0 ? (
          <CalculatorTable
            showInputs={false}
            termsSelectable
            regionId={regionId}
            promotionCode={promotionCode}
            unitPrice={parseFloat(unitPrice) + getIncludedAdjustmentsSum()}
            taxRate={parseFloat(taxRate)}
            taxRateString={taxRateString}
            isTaxExempt={isTaxExempt}
            atLeast14Wide={wFt >= 14.0}
            atLeast16Wide={wFt >= 16.0}
            includeLDW={optedOutOfDamageLiabilityWaiver === false}
            customDownPayment={initialPaymentAmount}
            sideFees={ui_helpers.sideFeeSum(priceAdjustments)}
            sideFeePayment={sideFeePayment}
            productType={productType}
            onUpdateSelectedTerm={onSelectedTermChange}
            selectedTermId={selectedTermInfo ? selectedTermInfo.contractTermId : null}
            onSelectedTermUpdate={(t) => {
              setSelectedTermInfo(t);
              if (t && t.dlwAmount) {
                setDamageLiabilityWaiverAmount(parseFloat(t.dlwAmount));
              }
            }}
            companyId={companyId}
            checkTermPromoAvailability={() => checkTermPromoAvailability(promotionCode)}
            manufacturerId={manufacturerId}
            showRTOCompanyName
          />
        ) : (
          <div className="gigantic">Provide details to the left to see term options.</div>
        )}
      </Col>
    </Row>
  );
}
